import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { TextArea } from '@/components/ui/text-area';
import { cn } from '@/lib/utils';

import {
  additionalInfoAtom,
  checkboxesSettingsAtom,
  roomModeSettingsAtom,
  updateSingleCheckboxAtom,
} from '../../atoms/settings';
import { ROOM_MODE_OPTIONS_WITH_NO_CHECKBOXES } from '../../constants/room-mode';
import {
  FLOOR_AND_WALL_CHECKBOX_KEYS,
  ROOM_MODE_CHECKBOX_CONFIG,
} from '../../constants/style-settings';
import { SettingsSectionHeader } from './settings-section-header';

export const AdditionalInfo = () => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(true);
  const [additionalInfo, setAdditionalInfo] = useAtom(additionalInfoAtom);
  const checkboxes = useAtomValue(checkboxesSettingsAtom);
  const mode = useAtomValue(roomModeSettingsAtom);
  const updateCheckbox = useSetAtom(updateSingleCheckboxAtom);

  const isModeWithoutCheckboxes = ROOM_MODE_OPTIONS_WITH_NO_CHECKBOXES.includes(mode);

  useEffect(() => {
    const { checked, editable } = ROOM_MODE_CHECKBOX_CONFIG[mode];

    FLOOR_AND_WALL_CHECKBOX_KEYS.forEach((option) => {
      updateCheckbox(option, checked, editable);
    });

    setIsOpen(!isModeWithoutCheckboxes);
  }, [isModeWithoutCheckboxes, mode, updateCheckbox]);

  return (
    <div
      className={cn(
        'w-full rounded-sm border border-slate-200 bg-white sm:w-[287px]',
        isModeWithoutCheckboxes && 'bg-slate-50 text-slate-400'
      )}
    >
      <SettingsSectionHeader
        label={t('additionalDetails')}
        value={!isModeWithoutCheckboxes ? additionalInfo.trim() : undefined}
        isOpen={isOpen}
        disabled={isModeWithoutCheckboxes}
        onClick={() => setIsOpen((current) => !current)}
      />

      {isOpen && (
        <div className="space-y-3 px-3 pb-3">
          <TextArea
            value={additionalInfo}
            disabled={isModeWithoutCheckboxes}
            onChange={(event) => setAdditionalInfo(event.target.value)}
            placeholder={t('additionalDetailsPlaceholder')}
            color="primary"
            rows={2}
            className="h-[58px] w-full resize-none overflow-y-auto rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 outline-none placeholder:text-slate-400 focus-visible:border-primary-light"
            wrapperClassName="overflow-hidden"
          />

          <div className="space-y-2">
            {checkboxes.map((checkbox) => {
              return (
                <Checkbox
                  key={checkbox.value}
                  label={t(checkbox.value)}
                  checked={checkbox.checked}
                  disabled={!checkbox.editable}
                  className="size-5"
                  onCheckedChange={(checked) => {
                    updateCheckbox(checkbox.value, checked === true, checkbox.editable);
                  }}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
